import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, LogIn, Plus, LayoutDashboard, Building2, Briefcase, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Logo } from '@/components/brand/Logo';
import { useData } from '@/hooks/useData';
import { cn } from '@/lib/utils';

export const NAV = [
  { to: '/', label: 'Início', end: true },
  { to: '/empresas', label: 'Empresas' },
  { to: '/oportunidades', label: 'Oportunidades' },
  { to: '/categorias', label: 'Categorias' },
  { to: '/sobre', label: 'Sobre a plataforma' },
];

const SearchCommand = ({ open, setOpen }) => {
  const { publicCompanies, publicOpportunities, categories } = useData();
  const navigate = useNavigate();
  const go = (to) => {
    setOpen(false);
    navigate(to);
  };
  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Buscar empresas, oportunidades ou categorias..." data-testid="global-search-input" />
      <CommandList className="max-h-[60vh]">
        <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
        <CommandGroup heading="Empresas (fictícias)">
          {publicCompanies.slice(0, 40).map((c) => (
            <CommandItem key={c.id} value={`${c.name} ${c.location.city} ${c.tagline}`} onSelect={() => go(`/empresas/${c.id}`)} data-testid={`search-result-company-${c.id}`}>
              <Building2 className="text-primary" /> {c.name} <span className="ml-auto text-xs text-muted-foreground">{c.location.city} · {c.location.uf}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Oportunidades (exemplos)">
          {publicOpportunities.slice(0, 40).map((o) => (
            <CommandItem key={o.id} value={`${o.title} ${o.location.city}`} onSelect={() => go(`/oportunidades/${o.id}`)}>
              <Briefcase className="text-[#ff6b84]" /> <span className="truncate">{o.title}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Categorias">
          {categories.filter((c) => c.active).map((c) => (
            <CommandItem key={c.id} value={`categoria ${c.name}`} onSelect={() => go(`/empresas?categoryId=${c.id}`)}>
              <Tag /> {c.name}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const { user } = useData();
  const location = useLocation();

  useEffect(() => setMenu(false), [location.pathname]);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const linkCls = ({ isActive }) => cn('relative rounded-md px-3 py-2 text-sm transition-colors hover:text-foreground', isActive ? 'text-foreground after:absolute after:inset-x-3 after:-bottom-[13px] after:h-px after:bg-primary after:shadow-[0_0_10px_#22d3ee]' : 'text-muted-foreground');

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[rgba(7,8,11,0.82)] backdrop-blur-md" data-testid="site-header">
      <div className="container-x flex h-16 items-center gap-4">
        <Link to="/" className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="Brasil Livre Business — início" data-testid="header-logo-link">
          <Logo />
        </Link>
        <nav className="mx-auto hidden items-center gap-1 lg:flex" aria-label="Principal">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={`nav-${n.to === '/' ? 'inicio' : n.to.slice(1)}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)} aria-label="Pesquisar" data-testid="header-search-button" className="text-muted-foreground hover:bg-white/5 hover:text-foreground">
            <Search />
          </Button>
          {user ? (
            <Button asChild variant="secondary" className="hidden border border-white/10 sm:inline-flex" data-testid="header-dashboard-button">
              <Link to="/painel"><LayoutDashboard /> Meu painel</Link>
            </Button>
          ) : (
            <Button asChild variant="secondary" className="hidden border border-white/10 sm:inline-flex" data-testid="header-login-button">
              <Link to="/entrar"><LogIn /> Entrar</Link>
            </Button>
          )}
          <Button asChild className="btn-glow hidden md:inline-flex" data-testid="header-register-company-button">
            <Link to="/cadastrar-empresa"><Plus /> Cadastrar empresa</Link>
          </Button>
          <Sheet open={menu} onOpenChange={setMenu}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Abrir menu" data-testid="mobile-menu-button">
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] border-white/10 bg-[#090c12]">
              <SheetHeader>
                <SheetTitle className="text-left"><Logo /></SheetTitle>
              </SheetHeader>
              <nav className="mt-8 flex flex-col gap-1" aria-label="Menu móvel" data-testid="mobile-menu-nav">
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => cn('rounded-md px-3 py-3 text-base transition-colors', isActive ? 'bg-white/5 text-foreground' : 'text-muted-foreground hover:bg-white/5')} data-testid={`mobile-nav-${n.to === '/' ? 'inicio' : n.to.slice(1)}`}>
                    {n.label}
                  </NavLink>
                ))}
                <NavLink to="/comunidade" className="rounded-md px-3 py-3 text-base text-muted-foreground hover:bg-white/5">Comunidade</NavLink>
              </nav>
              <div className="mt-8 flex flex-col gap-2">
                {user ? (
                  <Button asChild variant="secondary" className="border border-white/10"><Link to="/painel"><LayoutDashboard /> Meu painel</Link></Button>
                ) : (
                  <Button asChild variant="secondary" className="border border-white/10" data-testid="mobile-login-button"><Link to="/entrar"><LogIn /> Entrar</Link></Button>
                )}
                <Button asChild className="btn-glow" data-testid="mobile-register-company-button"><Link to="/cadastrar-empresa"><Plus /> Cadastrar empresa</Link></Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <SearchCommand open={open} setOpen={setOpen} />
    </header>
  );
};
